import querystring from "query-string";

import type {
  CreateContestRequestBody,
  GetConsumerLoyaltyProfileResponse,
  GetRewardExtendedResponse,
  GetRewardResponse,
} from "@jti/evo-client";
import { getConfig } from "@jti/fe-common";
import type {
  ArticleDataType,
  ArticleReadActionReturnType,
  BuyPrizeElementResponseType,
  CampaignOrderData,
  CustomerPointsHistoryResponseType,
  CustomerReferralHistoryResponseType,
  GetAchievementsResponse,
  GetArticlesPayloadType,
  GetArticlesResponseType,
  GetContestsResponseType,
  GetEarnedPointsResponseType,
  GetEarningRulePointsReturnType,
  GetEligibleCampaignsResponseType,
  GetPointsResponseType,
  GetPrizesInputType,
  GetPrizesResponseType,
  GetProductPointsResponseType,
  GetProductPointsReturnType,
  GetQrCodeDetailsPayload,
  GetQrCodeDetailsResponseType,
  GetRewardExtendedPayload,
  GetRewardFiltersResponseType,
  GetRewardPayload,
  GetTiersResponseType,
  GetUserTierDataResponseType,
  GetUserTierResponseType,
  LoyaltyStatusResponseType,
  QuizCompleteActionReturnType,
  ScanQrActionReturnType,
} from "@jti/rest-api-types";

import calculateProductPoints from "../../../utils/calculateProductPoints";
import sortByNumber from "../../../utils/sortByNumber";
import type { RestResponseType } from "../../Rest";
import { Rest } from "../../Rest";
import type { LoyaltyApiContact, LoyaltySummaryResponse } from "../LoyaltyApi.types";
import { LoyaltyApiEvo } from "../LoyaltyApiEvo";

const createEmptyPointsResponse = (): GetPointsResponseType => ({
  points: 0,
  expire_at: "",
  pending_points: 0,
  points_to_expire: 0,
});

const normalizePointsResponse = (response: GetPointsResponseType): GetPointsResponseType => {
  const { statusCode: _ignored, ...rest } = response as GetPointsResponseType & { statusCode?: number };
  return rest;
};

const normalizeTierResponse = (response: GetUserTierResponseType): GetUserTierResponseType => {
  const { statusCode: _ignored, ...rest } = response as GetUserTierResponseType & { statusCode?: number };
  return rest;
};

/** @deprecated Legacy proxy implementation — superseded by EVO. To be deleted after production verification. */
export class LoyaltyApiLegacy extends Rest implements LoyaltyApiContact {
  readonly name = "LoyaltyApi";
  constructor(
    baseUrl: string,
    protected tenantToken: string
  ) {
    super(baseUrl);
    this.setCommonHeader("X-DES-AK-TENANT-TOKEN", this.tenantToken);
  }

  getArticles({ uuid, ciam_token, typedIdentifiers }: GetArticlesPayloadType): Promise<GetArticlesResponseType> {
    const options: RequestInit = {
      method: "GET",
    };

    if (uuid && ciam_token) {
      options.headers = { "X-DES-CONSUMER-IAM-TOKEN": ciam_token };
    }
    const idsParams = typedIdentifiers?.article || [];
    const urlExtension = idsParams?.length ? `?ids=${idsParams.join(",")}` : "";
    const url = uuid && ciam_token ? `/customer/${uuid}/articles` : `/articles`;

    return this.request<ArticleDataType[]>(`${url}${urlExtension}`, options, true, false, this.getTimeoutValue())
      .then(({ data }) => data.map((article) => ({ article_type: article.article_type || "article", ...article })))
      .catch((err) => {
        console.error(err);
        throw err;
      });
  }

  getStatus(): Promise<boolean> {
    return this.request<LoyaltyStatusResponseType>(
      "/status",
      {
        method: "GET",
      },
      false,
      true,
      this.getTimeoutValue()
    )
      .then((response) => response.isLoyaltyActive)
      .catch(() => false);
  }

  getPoints(uuid: string, ciam_token: string, useLocalCache = true): Promise<GetPointsResponseType> {
    const failureObject: GetPointsResponseType = {
      points: 0,
      expire_at: "",
      pending_points: 0,
      points_to_expire: 0,
    };

    return this.request<GetPointsResponseType>(
      `/points?uuid=${uuid}`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "GET",
      },
      false,
      useLocalCache,
      this.getTimeoutValue()
    )
      .then((response) => {
        if (response?.points) {
          return response;
        }

        return failureObject;
      })
      .catch(() => failureObject);
  }

  async getLoyaltySummary(uuid: string, ciam_token: string, useLocalCache = true): Promise<LoyaltySummaryResponse> {
    const [pointsResult, tierResult] = await Promise.allSettled([
      this.getPoints(uuid, ciam_token, useLocalCache),
      this.getUserTier(uuid, ciam_token),
    ]);

    const pointsData =
      pointsResult.status === "fulfilled" ? normalizePointsResponse(pointsResult.value) : createEmptyPointsResponse();
    const tierData = tierResult.status === "fulfilled" ? normalizeTierResponse(tierResult.value) : null;

    return {
      points: pointsData,
      tier: tierData,
    };
  }

  getProductPoints(
    sku = "",
    quantity = 1,
    uuid: string | null = null,
    ciam_token: string | null = null
  ): Promise<GetProductPointsReturnType> {
    const failureObject = {
      rules: [],
      points: 0,
      time_limited: false,
      time_to: "",
    };

    const requestURI = uuid && ciam_token ? `/customer/${uuid}/product/${sku}/points` : `/product/${sku}/points`;
    const options = ciam_token ? { headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token } } : {};

    return this.request<GetProductPointsResponseType>(requestURI, options, true, true, this.getTimeoutValue())
      .then((response) => {
        if (response?.rules?.length > 0) {
          return {
            points: calculateProductPoints(response.rules, quantity),
            rules: response.rules,
            time_to: response.time_to,
            time_limited: response.time_limited,
          };
        }

        return failureObject;
      })
      .catch(() => failureObject);
  }

  getEligibleCampaigns(
    uuid: string,
    ciam_token: string,
    order?: CampaignOrderData
  ): Promise<GetEligibleCampaignsResponseType[]> {
    return this.request<GetEligibleCampaignsResponseType[]>(
      `/customers/${uuid}/eligible-campaigns`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "POST",
        ...(order && { body: JSON.stringify({ order: order }) }),
      },
      true,
      false,
      this.getTimeoutValue()
    )
      .then((response) => (response ? sortByNumber(response.data, "price") : []))
      .catch(() => []);
  }

  getPointsHistory(
    consumer_uuid: string,
    ciam_token: string,
    perPage = 10,
    page = 1
  ): Promise<CustomerPointsHistoryResponseType> {
    return (
      this.request<CustomerPointsHistoryResponseType>(
        `/customers/${consumer_uuid}/points/history?perPage=${perPage}&page=${page}`,
        {
          headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
          method: "GET",
        },
        false,
        false,
        this.getTimeoutValue()
      )
        // FIXME: Type casting
        .then((response: CustomerPointsHistoryResponseType) => response)
        // FIXME: how can error be correct response?
        .catch((response: CustomerPointsHistoryResponseType) => response)
    );
  }

  getReferralHistory(
    consumer_uuid: string,
    ciam_token: string,
    perPage = 5,
    page = 1
  ): Promise<CustomerReferralHistoryResponseType> {
    return this.request<CustomerReferralHistoryResponseType>(
      `/customers/${consumer_uuid}/events/referrer?perPage=${perPage}&page=${page}`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "GET",
      },
      true,
      false,
      this.getTimeoutValue()
    )
      .then((response: CustomerReferralHistoryResponseType) => response)
      .catch((response: CustomerReferralHistoryResponseType) => {
        return response;
      });
  }

  getTiers(): Promise<RestResponseType<GetTiersResponseType[]>> {
    return this.request<GetTiersResponseType[]>(
      `/tiers`,
      {
        headers: { "X-DES-AK-TENANT-TOKEN": this.tenantToken },
      },
      false,
      false,
      this.getTimeoutValue()
    );
  }

  getUserTierData(consumer_uuid: string, ciam_token: string): Promise<RestResponseType<GetUserTierDataResponseType>> {
    return this.request<GetUserTierDataResponseType>(
      `/profile/${consumer_uuid}`,
      {
        headers: { "X-DES-AK-TENANT-TOKEN": this.tenantToken, "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
      },
      true,
      false,
      this.getTimeoutValue()
    );
  }

  getEarnedPoints(order_id: string, ciam_token: string): Promise<number> {
    return this.request<GetEarnedPointsResponseType>(
      `/orders/${order_id}`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token, "X-DES-AK-TENANT-TOKEN": this.tenantToken },
        method: "GET",
      },
      true,
      false,
      this.getTimeoutValue()
    )
      .then((response: GetEarnedPointsResponseType) => response.points)
      .catch(() => 0);
  }

  getPrizes(payload: GetPrizesInputType): Promise<RestResponseType<GetPrizesResponseType>> {
    const requestURI = `/prizes`;
    const { is_virtual, ...restPayload } = payload;
    const queryPayload = is_virtual === null || is_virtual === undefined ? restPayload : { ...restPayload, is_virtual };
    const queryParamsStringified = querystring.stringify(queryPayload);
    const fullRequestURI = queryParamsStringified ? requestURI.concat("?", queryParamsStringified) : requestURI;

    return this.request<GetPrizesResponseType>(fullRequestURI, { method: "GET" }, true, false, this.getTimeoutValue());
  }

  buyPrize(
    consumer_uuid: string,
    prize_uuid: string,
    qty: number,
    ciam_token: string
  ): Promise<RestResponseType<BuyPrizeElementResponseType>> {
    return this.request<BuyPrizeElementResponseType>(
      `/prizes/buy`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token, "X-DES-AK-TENANT-TOKEN": this.tenantToken },
        method: "POST",
        body: JSON.stringify({
          consumer_uuid,
          prize_uuid,
          qty,
        }),
      },
      true,
      false,
      this.getTimeoutValue()
    );
  }

  getEarningRulePoints(event_name: string, ciam_token: string, user_uuid: string | undefined): Promise<number> {
    return this.request<GetEarningRulePointsReturnType>(
      `/earningRules/${event_name}?uuid=${user_uuid}`,
      {
        method: "GET",
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token, "X-DES-AK-TENANT-TOKEN": this.tenantToken },
      },
      true,
      false,
      this.getTimeoutValue()
    )
      .then(({ points_amount }) => points_amount)
      .catch(() => 0);
  }

  getUserTier(consumer_uuid: string, ciam_token: string): Promise<GetUserTierResponseType> {
    return this.request<GetUserTierResponseType>(
      `/customer/${consumer_uuid}/tier`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token, "X-DES-AK-TENANT-TOKEN": this.tenantToken },
        method: "GET",
      },
      true,
      true,
      this.getTimeoutValue()
    );
  }

  getQrCodeDetails({ qrCodeId, ciam_token }: GetQrCodeDetailsPayload): Promise<GetQrCodeDetailsResponseType> {
    return this.request<GetQrCodeDetailsResponseType>(
      `/qr-code/${qrCodeId}`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token, "X-DES-AK-TENANT-TOKEN": this.tenantToken },
        method: "GET",
      },
      true,
      false,
      this.getTimeoutValue()
    );
  }

  readArticleAction(article_id: string, ciam_token: string): Promise<ArticleReadActionReturnType> {
    return this.request<ArticleReadActionReturnType>(
      "/articles/read",
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "POST",
        body: JSON.stringify({
          article_id,
        }),
      },
      true,
      false,
      this.getTimeoutValue()
    );
  }

  async scanQrAction(qr_id: string, ciam_token: string): Promise<ScanQrActionReturnType> {
    const res = await this.request<ScanQrActionReturnType>(
      "/qr-code/read",
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "POST",
        body: JSON.stringify({
          qr_id,
        }),
      },
      true,
      false,
      this.getTimeoutValue()
    );

    return res;
  }

  getAchievements(uuid: string, ciam_token: string): Promise<GetAchievementsResponse> {
    return this.request<GetAchievementsResponse>(
      `/achievements/${uuid}`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "GET",
      },
      true,
      false,
      this.getTimeoutValue()
    );
  }

  private getApiEvo() {
    const { EVO_DOMAIN, EVO_TENANT, IS_LOYALTY_ENABLED, ...config } = getConfig();
    if (!EVO_DOMAIN || !EVO_TENANT) {
      throw new Error("Missing required configuration for getReward");
    }
    const evo = new LoyaltyApiEvo(EVO_DOMAIN, EVO_TENANT, config["X-RECAPTCHA-ID"], IS_LOYALTY_ENABLED === "true");

    return evo;
  }

  async getReward({ reward_uuid }: GetRewardPayload): Promise<GetRewardResponse> {
    return await this.getApiEvo().getReward({ reward_uuid });
  }

  async getRewardExtended({
    consumer_uuid,
    reward_uuid,
  }: GetRewardExtendedPayload): Promise<GetRewardExtendedResponse> {
    return await this.getApiEvo().getRewardExtended({ consumer_uuid, reward_uuid });
  }

  getTimeoutValue(defaultTimeout = 5000): number {
    let timeout = defaultTimeout;
    const timeoutConfig = parseInt(getConfig()?.LOYALTY_REQUEST_TIMEOUT, 10);
    if (!isNaN(timeoutConfig)) {
      timeout = timeoutConfig;
    }

    return timeout;
  }

  async getContestStatus(uuid: string): Promise<GetContestsResponseType> {
    return await this.getApiEvo().getContestStatus(uuid);
  }

  async getLoyaltyProfile(
    uuid: string,
    ciam_token: string,
    skipCache?: boolean
  ): Promise<GetConsumerLoyaltyProfileResponse> {
    return await this.getApiEvo().getLoyaltyProfile(uuid, ciam_token, skipCache);
  }

  async postContest(uuid: string, ciam_token: string, contestSubmitData: CreateContestRequestBody): Promise<void> {
    return await this.getApiEvo().postContest(uuid, ciam_token, contestSubmitData);
  }

  async watchVideoAction(uuid: string, video_id: string) {
    return await this.getApiEvo().watchVideoAction(uuid, video_id);
  }

  async completeQuizAction(quiz_id: string, uuid: string): Promise<QuizCompleteActionReturnType> {
    return await this.getApiEvo().completeQuizAction(quiz_id, uuid);
  }

  async getRewardFilters(): Promise<GetRewardFiltersResponseType> {
    return await this.getApiEvo().getRewardFilters();
  }
}
